// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      return null;
    }

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Profile fetch timeout')), 10000)
    );

    try {
      const fetchPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]);
      if (error) throw error;
      if (data) setProfile(data);
      return data;
    } catch (err) {
      console.error('fetchProfile error:', err.message);
      return null;
    }
  }, []);

  // ── Auth state + profile sync (your existing logic) ──
  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user) {
          fetchProfile(session.user.id).catch(console.error);
        }
      } catch (err) {
        console.error('Auth init error:', err);
        if (isMounted) setLoading(false);
      }
    };

    initialize();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'INITIAL_SESSION') return;
        if (!isMounted) return;

        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id).catch(console.error);
        } else {
          setProfile(null);
        }
        setLoading(false);
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // ── Claim anonymous saves when user signs in (any method) ──
  useEffect(() => {
    const claimPendingSaves = async (userId) => {
  const fp = localStorage.getItem('vaibes_fingerprint');
  if (!fp || !userId) return;
  try {
    const { data: claimed, error } = await supabase.rpc('claim_pending_saves', { p_fingerprint: fp });
    if (error) throw error;
    if (claimed > 0) console.log(`✨ ${claimed} saved item(s) added to your wall`);
    localStorage.removeItem('vaibes_fingerprint');
  } catch (err) {
    console.error('Claim pending saves failed, will retry next load:', err);
  }
};

    // Page load — user may already be signed in (e.g., OAuth redirect return)
    const handleInitial = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        // Defer to avoid blocking auth init
        setTimeout(() => claimPendingSaves(session.user.id), 0);
      }
    };
    handleInitial();

    // Fresh sign-ins (email confirm, OAuth callback)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event !== 'SIGNED_IN') return;
        if (session?.user) {
          setTimeout(() => claimPendingSaves(session.user.id), 0);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []); // Empty deps — runs once

  const signInWithGoogle = useCallback(async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: 'https://vaibes.pro/app' }
    });
  }, []);

  const signInWithTwitter = useCallback(async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'twitter',
      options: { redirectTo: 'https://vaibes.pro/app' }
    });
  }, []);

  const signInWithEmail = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUpWithEmail = useCallback(async (email, password, displayName = 'New User') => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName } }
    });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const contextValue = useMemo(() => ({
    user,
    profile,
    loading,
    signInWithGoogle,
    signInWithTwitter,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    fetchProfile: () => fetchProfile(user?.id)
  }), [user, profile, loading, signInWithGoogle, signInWithTwitter, signInWithEmail, signUpWithEmail, signOut, fetchProfile]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);